/* Editorial: Hero carousel controller.

   The slides stack absolutely in the frame and crossfade. Below 990px one
   slide shows at a time; from 990px they show two at a time, as a pair of
   half-width portrait panels, and everything (autoplay, swipe, keys, dots)
   moves by pair. Autoplay, a horizontal swipe, a dot or the arrow keys move the
   index; only the selected slides are visible and clickable. Autoplay mirrors
   embla's `stopOnInteraction: false`: interaction resets the timer, it never
   stops permanently, and it stays off entirely under reduced motion, where CSS
   drops the transition so the swap is instant. */
(function () {
  'use strict';

  function setup(root) {
    var fade = root.querySelector('[data-hero-fade]');
    var region = root.querySelector('[data-hero-region]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-hero-dot]'));

    function reveal() {
      if (fade) fade.classList.add('is-ready');
    }

    if (!region || slides.length === 0) {
      reveal();
      return;
    }

    var delay = parseFloat(root.dataset.autoplayDelay);
    if (!isFinite(delay) || delay <= 0) delay = 6000;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var twoUp = window.matchMedia('(min-width: 990px)');
    var n = slides.length;
    var current = 0;
    var timer = null;
    var destroyed = false;

    /* How many steps the carousel has: slides on mobile, pairs on desktop. */
    function units() {
      return twoUp.matches && n > 1 ? Math.ceil(n / 2) : n;
    }

    /* The slides unit i shows: [left, right], right -1 when there is no pair.
       An odd slide count closes the last pair with the first slide. */
    function slidesFor(i) {
      if (!twoUp.matches || n < 2) return [i, -1];
      var left = i * 2;
      var right = left + 1;
      if (right > n - 1) right = 0;
      return [left, right];
    }

    function select(i) {
      var u = units();
      current = ((i % u) + u) % u;
      var pair = slidesFor(current);
      slides.forEach(function (slide, k) {
        slide.classList.toggle('is-selected', k === pair[0]);
        slide.classList.toggle('is-selected-right', k === pair[1]);
        /* Panel geometry is sticky, so a slide keeps its panel while it fades
           out; only the incoming pair is re-sided. */
        if (k === pair[0]) slide.classList.remove('is-panel-right');
        if (k === pair[1]) slide.classList.add('is-panel-right');
      });
      dots.forEach(function (dot, k) {
        dot.hidden = k >= u;
        dot.setAttribute('aria-selected', k === current ? 'true' : 'false');
      });
    }

    function autoplayAllowed() {
      return !destroyed && !document.hidden && !reducedMotion.matches && units() > 1;
    }

    function pause() {
      if (timer === null) return;
      clearTimeout(timer);
      timer = null;
    }

    function schedule() {
      pause();
      if (!autoplayAllowed()) return;
      timer = setTimeout(function () {
        timer = null;
        go(current + 1);
      }, delay);
    }

    function go(i) {
      select(i);
      schedule();
    }

    /* --- Swipe --------------------------------------------------------------
       Horizontal intent only: a mostly-vertical drag is the page scrolling
       past the hero, and touch-action: pan-y keeps that working. */
    var startX = 0;
    var startY = 0;
    var tracking = false;
    var swiped = false;

    region.addEventListener(
      'pointerdown',
      function (event) {
        startX = event.clientX;
        startY = event.clientY;
        tracking = true;
        swiped = false;
      },
      { passive: true }
    );

    region.addEventListener(
      'pointerup',
      function (event) {
        if (!tracking) return;
        tracking = false;
        var dx = event.clientX - startX;
        var dy = event.clientY - startY;
        if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
        swiped = true;
        go(dx < 0 ? current + 1 : current - 1);
      },
      { passive: true }
    );

    region.addEventListener(
      'pointercancel',
      function () {
        tracking = false;
      },
      { passive: true }
    );

    /* The whole slide is an <a>: a swipe must not follow it. */
    region.addEventListener(
      'click',
      function (event) {
        if (!swiped) return;
        swiped = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    region.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          go(current - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          go(current + 1);
        }
      },
      true
    );

    dots.forEach(function (dot, k) {
      dot.addEventListener('click', function () {
        go(k);
      });
    });

    function onVisibilityChange() {
      if (document.hidden) {
        pause();
      } else {
        schedule();
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    function onMotionChange() {
      if (reducedMotion.matches) {
        pause();
      } else {
        schedule();
      }
    }
    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', onMotionChange);
    }

    function onTwoUpChange() {
      /* Read the slide on screen rather than deriving it: the query has already
         flipped, so slidesFor() would map the old index the new way. */
      var shown = 0;
      slides.forEach(function (slide, k) {
        if (slide.classList.contains('is-selected')) shown = k;
      });
      select(twoUp.matches ? Math.floor(shown / 2) : shown);
      schedule();
    }
    if (typeof twoUp.addEventListener === 'function') {
      twoUp.addEventListener('change', onTwoUpChange);
    }

    function onSectionUnload(event) {
      if (!event.target || !event.target.contains(root)) return;
      destroyed = true;
      pause();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      if (typeof reducedMotion.removeEventListener === 'function') {
        reducedMotion.removeEventListener('change', onMotionChange);
      }
      if (typeof twoUp.removeEventListener === 'function') {
        twoUp.removeEventListener('change', onTwoUpChange);
      }
    }
    document.addEventListener('shopify:section:unload', onSectionUnload);

    select(0);
    reveal();
    schedule();
  }

  function init() {
    document.querySelectorAll('[data-hero-carousel]').forEach(function (root) {
      if (root.dataset.heroCarouselInit) return;
      root.dataset.heroCarouselInit = '1';
      setup(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
